import { ref, type TokenSet } from "../emit/layers.ts";

/**
 * The component layer (§ Token architecture, layer 3): the values a single component needs
 * that no semantic token expresses. Sizes come from § The shell, § Focus ring, § Overlays
 * (the side peek bounds of T060), § Iconography and `DESIGN.md` § Components and the shell values measured from the owner's reference on 2026-09-13.
 */
export const components: TokenSet = {
  "border-width": "1px",
  "focus-ring-width": "2px",
  "focus-ring-offset": "2px",
  /** The room a scroll container leaves at its edges so a focused control's ring, offset and width, is not clipped there (2026-09-17). */
  "focus-ring-room": "4px",
  /** The dot at each end of a drop line (contracts/design-system.md § Drop indicator, 2026-10-15); the line itself is `--focus-ring-width` thick. */
  "drop-dot-size": "6px",
  // 2026-09-13: measured from the owner's reference, .impeccable/references/shell-reference.png.
  // 2026-09-20, by the owner's decision: the rail's tiles are upright rectangles with the mode's
  // word under its icon, so the rail widens from 60 px to hold a 56 px tile with 8 px either side.
  "rail-width": "72px",
  // 2026-09-14: one spacing between stacked chrome items, the rail's mode tiles and the sidebar rows alike.
  "chrome-item-spacing": ref("space-2"),
  "rail-item-width": "56px",
  "rail-item-height": "64px",
  "rail-item-radius": "10px",
  // The room between a tile's icon and its word.
  "rail-item-gap": ref("space-1"),
  // 20 px in every tile, mode and bottom cluster alike (2026-09-20; 24 px read as scaled up beside the sidebar's 16 px icons).
  "rail-icon-size": ref("icon-md"),
  "chrome-icon-stroke": "1.5",
  "indicator-width": "3px",
  "indicator-height": "20px",
  "indicator-radius": "2px",
  "caret-width": ref("space-3"),
  "caret-height": ref("space-2"),
  // 2026-09-28, by the owner's decision ("use the logos everywhere in the platform"): the logo's height wherever it is drawn with its word, the sign-in card, the setup and onboarding cards, the page outside the shell and the email, and the B mark's box in the rail's head (DESIGN.md § Brand, contracts/design-system.md § Brand).
  "brand-logo-height": "28px",
  "brand-mark-size": "28px",
  // 2026-09-29, by the owner's review of the rail ("if we hover over the logo in the top left rail, it should animate or shimmer or something?"): the one pass of the B mark's sheen, longer than the four duration tiers because a sweep across a mark reads at this pace and not at 250 ms; the reduced-motion override zeroes it with them (DESIGN.md § Brand, contracts/design-system.md § Motion).
  "brand-sheen": "600ms",
  // 2026-10-15, by the owner's decision on the agent activity language (contracts/design-system.md § Agent activity, § Motion, The third loop): the working indicator's three dots step their opacity over this loop, the skeleton loop's length, each a third of it after the one before; the reduced-motion override zeroes it with the other loops, and the dots then stand still at full opacity with the word beside them.
  "working-loop": "1200ms",
  // The tallest the approval card's well of the proposed action grows before it scrolls (§ Approval card, 2026-10-15).
  "approval-well-max": "160px",
  // The assistant panel after the owner's redesign of 2026-10-16 (§ Assistant panel): the answer's reading line, a person's bubble's widest share of the thread, and a citation chip's size (18 px since 2026-10-20).
  "assistant-reading-line": "1.375rem",
  "assistant-bubble-max": "85%",
  "assistant-marker-size": "18px",
  // 2026-10-20, the approved redesign (§ Assistant panel): a person's bubble's radius, the composer's, and a bordered list's or card's in the thread (the record rows, the suggestions, a receipt, a source card).
  "assistant-bubble-radius": "16px",
  "assistant-composer-radius": "14px",
  "assistant-list-radius": "10px",
  // The panel's small glyphs and marks (2026-10-20): a source kind's or a declined line's glyph, a field's arrow and a target's state, the round mark of a receipt and of a source's kind, the greeting's sparkle, and the greeting's title step.
  "assistant-glyph-xs": "10px",
  "assistant-glyph-sm": "12px",
  "assistant-badge-size": "18px",
  "assistant-mark-size": "22px",
  "assistant-greeting-size": "1.125rem",
  // The agent activity rows (§ Agent activity): a bare row's height, the turn's work line and each of its steps (28 px, 2026-10-20) with their 14 px icons, the three dots' size and gap, and the session status pill's height and dot.
  "activity-row-height": "32px",
  "activity-step-height": "28px",
  "activity-icon-size": "14px",
  // The dot that marks what asks for the reader (2026-10-20, the assistant panel's redesign): the amber dot of a request waiting for approval, in the approval card's head and on a conversation that holds one.
  "attention-dot-size": "7px",
  "working-dot-size": "4px",
  "working-dot-gap": "2px",
  "session-pill-height": "24px",
  "session-dot-size": "6px",
  // The composer surface (§ Composer, 2026-10-15; the assistant panel's redesign of 2026-10-20): the box's padding, the send circle's size and the most lines the text area grows to before it scrolls.
  "composer-padding": ref("space-3"),
  "send-button-size": "28px",
  "composer-max-lines": "8",
  "sidebar-width": "260px",
  "sidebar-width-min": "260px",
  "sidebar-width-max": "360px",
  "sidebar-padding-x": ref("space-4"),
  "sidebar-card-height": "40px",
  "sidebar-card-radius": "10px",
  "sidebar-mark-size": "22px",
  "sidebar-mark-radius": "6px",
  "sidebar-row-height": "36px",
  "sidebar-row-radius": ref("radius-md"),
  "sidebar-row-padding-x": "10px",
  "sidebar-row-gap": "10px",
  // 2026-10-03, by the owner's review of the Clients sidebar ("should be aligned with the collapse icon"): the slot at a row's end, before its inline padding, whose centre is the one axis of every trailing mark: a count, a heading's count, a row's country tag, a branch's disclosure chevron and a section label's control. A mark wider than the slot overflows it equally on both sides, so its centre stays on the axis.
  "sidebar-trailing-size": ref("icon-sm"),
  // The same review: a collapsible heading's flag or globe sits in a slot of this width after its chevron, so its name starts at the same x whatever the platform draws for the flag, and the rows under it start there too.
  "sidebar-heading-mark-size": ref("icon-md"),
  "sidebar-label-weight": "500",
  "sidebar-label-tracking": "0.06em",
  "sidebar-section-space-before": "20px",
  "sidebar-section-space-after": "6px",
  "sidebar-lock-size": "12px",
  "sidebar-avatar-size": "36px",
  "sidebar-avatar-radius": ref("radius-md"),
  "sidebar-name-weight": "600",
  "surface-inset": ref("space-3"),
  "surface-radius": "16px",
  "tab-track-radius": ref("radius-lg"),
  "tab-active-radius": "10px",
  "page-padding": ref("space-6"),
  // 2026-09-29, by the owner's review of the live site: every page opens with one head (`PageHead`, contracts/design-system.md § Page head), so the sheet's top to the title, the title block to what follows it (a toolbar, a row of tabs or the content), and a toolbar or tab row to the content under it are one set of steps on every page ("we kind wanna fill the page", and the spacing that differed between the Dashboard, All tasks, My work and the Inbox).
  "page-head-start": ref("page-padding"),
  "page-head-gap": ref("space-5"),
  "page-head-end": ref("space-5"),
  /** The title block's own rhythm: the title row to the meta row or the lede under it. */
  "page-head-meta-gap": ref("space-2"),
  "peek-width": "480px",
  "peek-width-min": "380px",
  "peek-width-max": "720px",
  // 2026-09-27, by the owner's decision: the authentication screens' column in the work sheet (contracts/design-system.md § Component sizes).
  "login-width": "400px",
  // 2026-09-27, by the owner's decision: the sign-in card's outer radius, the work sheet's radius plus the inset the sheet sits in by, so the card's corners run concentric with the sheet's inside it (24 px today; contracts/design-system.md § Component sizes).
  "login-radius": "calc(var(--surface-radius) + var(--surface-inset))",
  "dialog-width-sm": "400px",
  "dialog-width-md": "560px",
  "dialog-width-lg": "760px",
  /** The item dialog (2026-09-18, by the owner's decision: 1,080 by 720 buried the sub-tasks and the checklist): up to 1,400 by 960, capped at `--dialog-xl-share` of the viewport. */
  "dialog-width-xl": "1400px",
  "dialog-height-xl": "960px",
  "dialog-xl-share": "92%",
  // 2026-09-14: aligned to Spectrum UI by the owner's UI review (contracts/design-system.md § Component sizes).
  "toast-width": "384px",
  "toast-radius": "12px",
  "toast-icon-size": "20px",
  "button-padding-x-sm": ref("space-4"),
  "button-padding-x-md": ref("space-5"),
  "button-padding-x-lg": ref("space-7"),
  "control-radius": ref("radius-sm"),
  "control-font-weight": "500",
  "menu-width-min": "128px",
  /** The room the search and filter field keeps for its typed text while applied-filter tokens fill the rest (2026-09-23). */
  "field-search-text-min": "10rem",
  /** The filter field (2026-09-27): the inset of its tokens inside its border, and the height of one line of tokens and text, so one line is the toolbar's small control height and each wrapped line adds one more. */
  "filter-field-pad": ref("space-2"),
  "filter-line-height": "calc(var(--control-height-sm) - var(--filter-field-pad) * 2 - var(--border-width) * 2)",
  /** The filter menu's width: a property list, a value list, or one month of the calendar with its inset. */
  "filter-menu-width": "288px",
  "menu-width-max": "320px",
  "menu-item-height": "32px",
  "palette-radius": "14px",
  "palette-footer-height": "44px",
  // 2026-09-15: where the command palette's frame starts and how tall it grows, as utilities read them (Spectrum UI migration, slice (b)).
  "palette-top": "15vh",
  "palette-height-max": "70vh",
  // 2026-09-15: the bulk bar's width below which its actions take a row of their own under the count, as a container query reads it (Spectrum UI migration, slice (c)).
  "container-bulk-actions": "72rem",
  "kbd-height": "24px",
  "kbd-padding-x": "6px",
  // 2026-09-15: the depth edge under a Spectrum UI Kbd Key cap, which the cap travels while pressed.
  "kbd-edge": "2px",
  "check-gap": "10px",
  "switch-width": "44px",
  "chip-height": "20px",
  // 2026-10-10: a collapsed board column. The strip is never shorter than its least height, and its name, which runs down it
  // under the header's toggle and the group's glyph, stops where the count after it, at most a chip tall, still ends on the
  // strip: the header row and the least height, less the header's top padding, the toggle, the glyph and the three gaps.
  "board-column-collapsed-min": "calc(var(--space-10) * 2)",
  "board-column-collapsed-label-max": "calc(var(--row-height) + var(--board-column-collapsed-min) - var(--space-2) - var(--target-min) - var(--icon-sm) - var(--space-3) * 3 - var(--chip-height))",
  "chip-height-md": "28px",
  "chip-padding-x": "10px",
  "chip-font-weight": "600",
  "avatar-stack-overlap": "0.28",
  // 2026-09-15 (contracts/design-system.md § Data display, ItemRow, by the owner's redesign after Plane's list): a list row's geometry.
  // The lead track holds the selection checkbox in a row and the collapse chevron in a group header.
  "item-row-lead": ref("target-min"),
  "item-row-gap": ref("space-3"),
  // The inline padding that puts the lead's 16 px glyph on the page padding: the padding less the glyph's inset inside its 24 px target; below --bp-md the page padding is --space-4.
  "item-row-padding": "calc(var(--page-padding) - (var(--target-min) - var(--icon-sm)) / 2)",
  "item-row-padding-narrow": "calc(var(--space-4) - (var(--target-min) - var(--icon-sm)) / 2)",
  // How far the rounded hover, selection and focus highlight sits in from the list's edges.
  "item-row-inset": ref("space-3"),
  // A group header glyph's inset, the chip padding, so it sits over the row glyphs; a list whose rows carry no glyph sets it to --space-0.
  "item-row-glyph-inset": ref("space-2"),
  // How far a card-frame row's content starts past the card's inset: none for a card's few rows, and the group header's caret
  // column and its gap where the rows sit under group headers, so the state glyph stands on the group's pill.
  "item-row-indent": ref("space-0"),
  // The head of a row (glyphs, title) keeps this width on the first line; when the properties do not fit beside it they wrap to a line of their own.
  "item-title-min": "320px",
  // 2026-10-03, by the owner's approval of the product review ("A two-line row on phones"), kept on 2026-10-04 when the rest of that restyle was withdrawn: a window narrower than this draws a list's rows on two lines, the due date, assignees and priority under the title, and no column names.
  "item-list-stack": "600px",
  /** The progress bar's track (2026-09-18, by the owner's redesign): a slim rounded track with the fill in the accent and the percent beside it, everywhere a share of work is shown. */
  "progress-track-height": "6px",
  /** The properties panel's label column (§ Item detail): wide enough for "Assignees" and "Start date" on one line, narrow enough that the values read as one column beside them. */
  "item-property-label": "112px",
  // Every property column of a list is at least this wide, so the columns run at even tracks as the owner's table reference does, and a measured value wider than the track widens its column alone.
  "item-column-min": "128px",
  /** The most a row's tags cell grows to: the tags shrink and truncate inside it, so the column past them stays put (2026-09-16). */
  "item-column-labels": "192px",
  /** The most a row's sprint cell grows to: a long sprint name truncates inside it, so a list that spans projects keeps the name column the width a project's list has (2026-09-17). */
  "item-column-cycle": "160px",
  /** The most a row's client cell grows to (020 FR-020-051, a display property since 2026-09-28): a long client name truncates inside it, as the sprint's name does. */
  "item-column-account": "160px",
  /** The most a row's milestone cell grows to (020 FR-020-186): a long stage name truncates inside it, as the sprint's name does. */
  "item-column-milestone": "160px",
  /** The most a row's custom field chip grows to (002): a long text value truncates inside it, as the sprint's name does. */
  "item-column-field": "192px",
  /** The most a row's creator or last editor cell grows to (2026-09-28): a long name truncates beside its avatar. */
  "item-column-actor": "160px",
  /** The most a row's parent cell grows to (2026-10-20, when the parent came to be named by its title, identifiers showing only on the task page and peek): a long title truncates inside it, as a custom field's text does. */
  "item-column-parent": "192px",
  // 2026-09-20 (002, the value editors of `field/editors/`): the widths a field's control takes.
  /** A progress value's bar at rest on the properties panel and in a table cell, beside its percent. */
  "field-progress-width": "96px",
  /** The popover form of a progress or location editor: the slider or the address over the coordinate pair. */
  "field-popover-width": "280px",
  /** The time-of-day field beside a timed date's day. */
  "field-time-width": "112px",
  /** The least a timed date's day trigger takes in a form before the time field wraps under it. */
  "field-date-min": "192px",
  /** The clear surface above a group's header, so groups stand apart as the owner's table reference has them (2026-09-16). */
  "group-gap": ref("space-6"),
  /** The space above and below a row's one-target-tall content, which makes the row `--row-height` tall. */
  "item-row-pad": "calc((var(--row-height) - var(--target-min)) / 2)",
  // 2026-09-18 (contracts/screens.md § Timeline layout, contracts/design-system.md § Component inventory, Timeline primitives): the timeline's geometry.
  /** One calendar day's width at each zoom; a column is one day, one week (7 days), one month or one quarter of these, so a bar's length is exact at every zoom. */
  "timeline-day-day": "40px",
  "timeline-day-week": "16px",
  "timeline-day-month": "4px",
  "timeline-day-quarter": "2px",
  /** The pinned column of rows at the inline start: the state dot and the title, as the list draws them. */
  "timeline-rows-width": "400px",
  "timeline-rows-width-narrow": "160px",
  /** The two bands of the sticky column header, months over days (or years over months). */
  "timeline-header-band": "28px",
  /** A bar's height inside its row, and the point a due-only task draws. */
  "timeline-bar-height": "calc(var(--row-height) - var(--space-5))",
  // A one-day task at the month and quarter zooms is two pixels of span; it keeps this much so it reads as a bar.
  "timeline-bar-min-width": "8px",
  "timeline-point-size": "12px",
  /** The diamond of a milestone (002 FR-002-044): as tall as the bar, so its row reads at the bar's weight. */
  "timeline-milestone-size": ref("timeline-bar-height"),
  /** The band rail under the column header: one lane for the window's sprints and one for its modules, and the least a band draws so a one-day one is still a band (002 § Timeline). */
  "timeline-band-height": "24px",
  "timeline-band-min-width": "10px",
  /** A bar narrower than this carries its label after it rather than inside. */
  /** The resize handle at each end of a bar, and the gap before a dependency arrow turns. */
  "timeline-handle-width": "8px",
  "timeline-arrow-gap": "12px",
  "timeline-arrow-width": "1.5px",
  "timeline-arrow-head": "6px",
  "timeline-today-width": "2px",
  // 2026-09-29 (022 § Amendment 2026-09-29, the Library; contracts/design-system.md § Library): a document card's least width in the section grid, which fills the row with as many as fit, the narrower least on a phone so two stand side by side at 390 px, and the shape of its thumbnail well, which a document's first page and a video's frame both fill from the top.
  "library-card-min": "calc(var(--picker-width-min) * 1.05)",
  "library-card-min-narrow": "calc(var(--picker-width-min) * 0.7)",
  "library-thumb-ratio": "16 / 10",
  // 2026-10-01, the meeting page's recording-first layout (contracts/screens.md § 018 Meeting agent, Meeting page; contracts/design-system.md § Component sizes): the side panel of chapters and transcript beside the main column, the approved mockup's width, and the tallest the player's picture stands in the main column, so the tabs under it stay on a laptop screen.
  "meeting-side-width": "380px",
  "meeting-player-max": "calc(var(--space-10) * 3)",
  // 2026-10-21 (contracts/screens.md § Home and § Overview, contracts/design-system.md § Home): the least height of a card of Home's and Overview's lists while the page fits one screen, its head, about two rows and its foot; a window too short to give every card in a column this much makes the page scroll rather than crush a card.
  "dash-card-min": "calc(var(--space-10) * 2)",
  // 2026-09-28 (contracts/screens.md § Calendar layout, contracts/design-system.md § Component inventory, CalendarGrid): the task calendar's geometry.
  /** A day's head, the day number and the "+" that creates on it, above the day's lanes. */
  "calendar-day-head": "28px",
  /** One entry's height, a chip or a bar, and the gap between two lanes. */
  "calendar-entry-height": "20px",
  "calendar-lane-gap": "2px",
  /** The dot before an entry's title, in its state's colour. */
  "calendar-dot": "8px",
  /** A day's number, and the disc today's number sits on. */
  "calendar-day-number": "20px",
  /** A week view's day at the least, so an empty week still reads as a week of days. */
  "calendar-week-min-height": "280px",
  /** The No date tray beside the grid on a wide surface. */
  "calendar-tray-width": "280px",
  /** The least a day's list takes, the popover of "+N more" and of Enter on a day. */
  "calendar-day-list-width": "260px",
  // 2026-09-29 (018 FR-018-282, contracts/design-system.md § Component inventory, TimeGrid): the meetings calendar's day and week views, after the Teams and Outlook calendars.
  /** One hour of a day's column; a block's least length, half an hour, is then 24 px, the least target. */
  "time-grid-hour": "48px",
  /** The hour gutter, holding the hours and, at its head, the timezone's name. 80 px since 2026-10-01 (the owner: "text is too close to the edge"): the present time, the gutter's widest label ("12:12 PM" in the heavier weight), filled the 64 px from edge to edge once its 12 px end inset was taken; 80 px leaves it the same clearance at its start. */
  "time-grid-gutter": "80px",
  // 2026-10-15, by the owner's decision (contracts/design-system.md § Calendar entries): an entry, a chip, a bar and a block alike,
  // is filled with its colour at this share over the sheet, mixed in OKLab (`color-mix(in oklab, <colour> 5%, --color-work-surface)`),
  // twice that under the pointer. The component computes the mix through the token package's OKLab arithmetic from these shares,
  // since the stylesheets admit no colour function; the 3 px edge and the 16 and 26 percent tints of 2026-09-29 went with it.
  "calendar-entry-wash": "0.05",
  "calendar-entry-wash-hover": "0.1",
  /** The least height of the time grid, which otherwise fills the page below its controls (2026-09-30): seven hours, past which the page scrolls. */
  "time-grid-min-height": "calc(var(--time-grid-hour) * 7)",
  "datepicker-cell": "36px",
  "datepicker-nav": "28px",
  "icon-sm": "16px",
  "icon-md": "20px",
  "icon-lg": "24px",
  /** The initials scale with the disc (2026-09-18, by the owner's decision: fixed type steps left the letters crowding a small disc and floating in a large one): the font size is this share of the diameter, at this weight and tracking, on every size. */
  "avatar-initials-ratio": "0.42",
  "avatar-initials-weight": "600",
  "avatar-initials-tracking": "0.02em",
  "avatar-xs": "16px",
  "avatar-sm": "20px",
  "avatar-md": "24px",
  "avatar-lg": "32px",
  "avatar-xl": "40px",
  /** The agent's badge on an avatar's corner (2026-10-03, `Avatar` `agentBadge`): the robot in Agent Violet, standing a quarter outside the disc. */
  "agent-mark-size": "10px",
  "target-min": "24px",
  "measure-prose": "76ch",
  "measure-unsafe": "24rem",
  "picker-width": "280px",
  "picker-width-min": "200px",
  "picker-list-max-height": "300px",
  // 2026-09-30 (§ Pickers, ColorPicker): the panel is nine 24 px swatches with 8 px between them and 12 px of inset, the palette's nine colours on one row; the custom view's saturation and brightness area fills that width at this height.
  "color-picker-width": "calc(var(--space-6) * 9 + var(--space-3) * 8 + var(--space-4) * 2)",
  "color-picker-area-height": "136px",
  // 2026-09-21 (the in-app file readers): the CSV table's column geometry.
  // A CSV column is never narrower than this, so a table of many columns scrolls sideways rather than
  // crushing every cell.
  "csv-column": "9rem",
  "skeleton-loop": "1200ms",
  // 2026-10-15, by the owner's decision (contracts/design-system.md § Loading, § Motion): how long a region waits before it shows
  // its skeleton, the length of the fade that resolves it, so a region has one time constant. A component token outside the
  // durations, the loops and the moments, so the reduced-motion override does not zero it: under reduced motion a fast response
  // still shows no skeleton, and only the fade after the hold is instant.
  "skeleton-delay": "150ms",
  // 2026-10-21 (contracts/design-system.md § Scroll fade): how deep a scroll region fades at an
  // edge its content continues past, about one line of body text, so the cut line reads as going on rather than as
  // half a line; 12 px until then, when it was the height of ScrollArea's edge shadow.
  "scroll-edge": "28px",
  // 2026-10-01, by the owner's report ("the pop up tips get hidden under the bar..."): the stacking
  // layers of what floats over the shell (contracts/design-system.md § Elevation, Layers), lowest
  // first. The shell's own layers (0 to 6 in shell.css) stay under all of them. The side peek covers
  // the screen below --bp-md; every overlay, dialogs, menus and popovers, lies over it; a tooltip is
  // one step over every overlay, so a tip shown from inside a dialog, a menu or a popover paints over
  // that surface whatever their order in the document; and the toasts lie over everything.
  "layer-peek": "40",
  "layer-overlay": "50",
  "layer-tooltip": "60",
  "layer-toast": "90",
};
